/**
 * WHAT A PERSON CAN POINT AT WITH @ IN THIS APP — relays, controllers, models, dashboards, widgets.
 *
 * Read from the same reconciled rosters the pages render from: no request, and never an entity the
 * person cannot see. Each item carries a CARD, the line the AI is told about it when the message is
 * sent — identity and structure only (names, ids, inputs, tile counts), never a live value. A
 * reading or a running state is something the AI must read with a tool, so the answer it gives is
 * one it can source.
 *
 * Every plant entity lives on a relay, and its id is only unique there, so each card names the
 * relay by name and by gateway id. That pair is exactly what the tools take, and it is the half the
 * AI most often got wrong when it had to find it.
 */
import { registerMentionProviders, setMentionFocus, type MentionItem, type MentionProvider } from '@vitesse/base-ui/ai';
import { gatewayLabel, type GatewayEntry } from '../managers/gateway-entry';
import type { Route } from '../shell/router';
import type { CatalogEntry } from '../../dashboards/catalog';

export interface PlantMentionScope {
  /** The org's whole roster. */
  entries(): readonly GatewayEntry[];
  /** The relay open in the browser, if any — what is on it ranks first. */
  selected(): GatewayEntry | undefined;
  /** Every widget a tile can name. */
  widgets(): readonly CatalogEntry[];
  /** Show a page of this app. */
  navigate(route: Route): void;
}

const q = (s: string): string => `"${s}"`;
const relayOf = (e: GatewayEntry): string => `relay ${q(gatewayLabel(e.gateway))} (gateway id: ${e.gateway.deviceFingerprint})`;
const plural = (n: number, one: string): string => `${n} ${one}${n === 1 ? '' : 's'}`;
/** A list that stays a line: the first few, then how many more. */
function few(items: readonly string[], max = 8): string {
  if (!items.length) return 'none';
  return items.length > max ? `${items.slice(0, max).join(', ')} and ${items.length - max} more` : items.join(', ');
}

export function plantMentionProviders(scope: PlantMentionScope): MentionProvider[] {
  const gateways: MentionProvider = {
    kind: 'gateway',
    items: () =>
      scope.entries().map((e): MentionItem => {
        const controllers = e.controllers.all;
        return {
          kind: 'gateway',
          id: e.gateway.deviceFingerprint,
          label: gatewayLabel(e.gateway),
          detail: plural(controllers.length, 'controller'),
          card:
            `${relayOf(e)} — controllers: ${few(controllers.map((c) => `${c.name} (${c.id})`))}; ` +
            `${plural(e.models.all.length, 'model')}; ${plural(e.dashboards.all.length, 'dashboard')}`,
        };
      }),
    open: (r) => scope.navigate({ view: 'gateway', gatewayId: r.id }),
  };

  const controllers: MentionProvider = {
    kind: 'controller',
    items: () =>
      scope.entries().flatMap((e) =>
        e.controllers.all.map(
          (c): MentionItem => ({
            kind: 'controller',
            id: c.id,
            gatewayId: e.gateway.deviceFingerprint,
            label: c.name || c.id,
            detail: gatewayLabel(e.gateway),
            card:
              `controller ${q(c.name || c.id)} (controller id: ${c.id}) on ${relayOf(e)} — protocol ${c.protocol}` +
              `${c.model ? `, model ${c.model}` : ''}${c.firmware ? `, firmware ${c.firmware}` : ''}, ${plural(c.symbolCount, 'symbol')}`,
          }),
        ),
      ),
    open: (r) => r.gatewayId && scope.navigate({ view: 'controller', gatewayId: r.gatewayId, controllerId: r.id }),
  };

  const models: MentionProvider = {
    kind: 'model',
    items: () =>
      scope.entries().flatMap((e) =>
        e.models.all.map((m): MentionItem => {
          const bound = (m.wiring?.inputs ?? []).map((p) => `${p.name} ← ${p.path || `= ${p.constant ?? ''}`}`);
          return {
            kind: 'model',
            id: m.id,
            gatewayId: e.gateway.deviceFingerprint,
            label: m.name || m.id,
            detail: gatewayLabel(e.gateway),
            // The wiring, not the state: inputs, outputs and what each input is connected to are what
            // a model IS. Whether it is running and which state it is in are read with models.list.
            card:
              `statechart model ${q(m.name || m.id)} (model id: ${m.id}) on ${relayOf(e)} — inputs: ${few(m.inputs ?? [])}; ` +
              `outputs: ${few(m.outputs ?? [])}; connected: ${few(bound, 6)}${m.description ? `; ${q(m.description)}` : ''}`,
          };
        }),
      ),
    open: (r) => r.gatewayId && scope.navigate({ view: 'model', gatewayId: r.gatewayId, modelId: r.id }),
  };

  const dashboards: MentionProvider = {
    kind: 'dashboard',
    items: () =>
      scope.entries().flatMap((e) =>
        e.dashboards.all.map(
          (d): MentionItem => ({
            kind: 'dashboard',
            id: d.id,
            gatewayId: e.gateway.deviceFingerprint,
            label: d.title || d.id,
            detail: gatewayLabel(e.gateway),
            card:
              `dashboard ${q(d.title || d.id)} (dashboard id: ${d.id}) on ${relayOf(e)} — ${plural(d.tileCount, 'tile')}` +
              `${d.description ? `; ${q(d.description)}` : ''}`,
          }),
        ),
      ),
    open: (r) => r.gatewayId && scope.navigate({ view: 'dashboard', gatewayId: r.gatewayId, dashboardId: r.id }),
  };

  // Not on a relay, and no page of their own — a widget is placed, not visited, so its chip opens nothing.
  const widgets: MentionProvider = {
    kind: 'widget',
    items: () =>
      scope.widgets().map(
        (w): MentionItem => ({
          kind: 'widget',
          id: w.id,
          label: w.title || w.id,
          detail: w.summary,
          card: `widget ${q(w.title || w.id)} (widget id: ${w.id}) — ${w.summary}; config keys: ${few(w.configKeys)}`,
        }),
      ),
  };

  return [gateways, controllers, models, dashboards, widgets];
}

/** Register this app's providers, and rank what is on the open relay first. Returns the undo. */
export function registerPlantMentions(scope: PlantMentionScope): () => void {
  const off = registerMentionProviders(...plantMentionProviders(scope));
  setMentionFocus(() => scope.selected()?.gateway.deviceFingerprint ?? '');
  return () => {
    off();
    setMentionFocus(null);
  };
}
